import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type OktaAuth from '@okta/okta-auth-js';
import { AuthContextService } from '@core/auth/context/auth-context.service';
import { ExternalAuthService } from '@core/auth/external/external-auth.service';
import { EXTERNAL_OKTA_AUTH } from '@core/auth/external/external-okta-auth.token';
import { toSafeInternalUri } from '@core/auth/safe-internal-uri';
import { LogRocketService } from '@core/observability/logrocket/logrocket.service';
import { engagementIdFromConnectPath } from '@core/observability/logrocket/logrocket.events';

@Component({
  selector: 'app-external-okta-callback-wrapper',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalOktaCallbackWrapperComponent {
  private readonly externalOktaAuth = inject<OktaAuth | null>(EXTERNAL_OKTA_AUTH, {
    optional: true,
  });
  private readonly authContext = inject(AuthContextService);
  private readonly externalAuthService = inject(ExternalAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logRocket = inject(LogRocketService);

  constructor() {
    void this.handleLoginRedirect();
  }

  private async handleLoginRedirect(): Promise<void> {
    if (this.route.snapshot.queryParamMap.has('error')) {
      await this.redirectToCompanySignInFailure();
      return;
    }

    if (!this.externalOktaAuth) {
      await this.redirectToCompanySignInFailure();
      return;
    }

    const originalUri = this.externalOktaAuth.getOriginalUri();

    try {
      await this.externalOktaAuth.handleLoginRedirect();
    } catch {
      await this.redirectToCompanySignInFailure();
      return;
    }

    this.authContext.setAuthType('external');
    this.recordLastLoginBestEffort();
    const destination = this.resolveSafeDestination(originalUri);
    this.logRocket.markFreshLogin(engagementIdFromConnectPath(destination));
    await this.router.navigateByUrl(destination, {
      replaceUrl: true,
    });
  }

  private recordLastLoginBestEffort(): void {
    const accessToken = this.externalOktaAuth?.getAccessToken?.();
    if (!accessToken) {
      return;
    }

    // Same best-effort path as password/IDX login (completeSuccessfulLogin).
    void this.externalAuthService.recordLastLogin(accessToken).catch(() => {
      // User is authenticated; a later login can re-record.
    });
  }

  private async redirectToCompanySignInFailure(): Promise<void> {
    await this.router.navigate(['/auth/login'], {
      queryParams: {
        error: 'company_sign_in_failed',
      },
      replaceUrl: true,
    });
  }

  private resolveSafeDestination(uri: string | null | undefined): string {
    return toSafeInternalUri(uri) ?? '/engagements';
  }
}
